import type { StatusTone } from '../constants/statusTokens';
import { ContainerStatus } from '../types/container.type';
import type { ContainerMapItem } from '../types/dashboard.type';
import { isValidCoordinate } from './geo';

export type ContainerAlarm = 'fire' | 'full' | 'tilt' | 'battery';

/** Most severe first. */
const ALARM_ORDER: ContainerAlarm[] = ['fire', 'full', 'tilt', 'battery'];

const KG_PER_UNIT: Record<string, number> = {
  kg: 1,
  kgs: 1,
  kilogram: 1,
  kilograms: 1,
  t: 1000,
  ton: 1000,
  tons: 1000,
  tonne: 1000,
  tonnes: 1000,
};

/**
 * Container capacity in kg (fill levels are always kg). Unknown units (litres,
 * cubic metres…) return null so no misleading percentage is computed.
 */
export function capacityInKg(capacity: number | null | undefined, unit: string | null | undefined): number | null {
  if (typeof capacity !== 'number' || !Number.isFinite(capacity) || capacity <= 0) return null;
  const factor = KG_PER_UNIT[(unit || 'kg').trim().toLowerCase()];
  return factor ? capacity * factor : null;
}

/** Fill as a share of capacity (0–1, may exceed 1 when over-filled), or null. */
export function fillShare(container: Pick<ContainerMapItem, 'fill_level' | 'capacity' | 'capacity_unit'>): number | null {
  const capacity = capacityInKg(container.capacity, container.capacity_unit);
  if (capacity === null || typeof container.fill_level !== 'number' || !Number.isFinite(container.fill_level)) {
    return null;
  }
  return Math.max(container.fill_level, 0) / capacity;
}

export function containerAlarms(container: Pick<ContainerMapItem, 'alarms'>): ContainerAlarm[] {
  const a = container.alarms ?? { fire: null, full: null, tilt: null, battery: null };
  return ALARM_ORDER.filter((key) => a[key] === true);
}

const AWAITING: ReadonlySet<string> = new Set([
  ContainerStatus.AWAITING_PICKUP,
  ContainerStatus.READY_FOR_PICKUP,
]);
const OUT_OF_SERVICE: ReadonlySet<string> = new Set([ContainerStatus.MAINTENANCE, ContainerStatus.INACTIVE]);

export function isAwaitingPickup(container: Pick<ContainerMapItem, 'status'>): boolean {
  return AWAITING.has(container.status);
}

export function isOutOfService(container: Pick<ContainerMapItem, 'status'>): boolean {
  return OUT_OF_SERVICE.has(container.status);
}

export function containerTone(container: Pick<ContainerMapItem, 'status' | 'alarms'>): StatusTone {
  const alarms = containerAlarms(container);
  if (alarms.includes('fire')) return 'failed';
  if (alarms.length > 0) return 'attention';
  if (isOutOfService(container)) return 'offline';
  if (isAwaitingPickup(container) || container.status === ContainerStatus.PENDING) return 'pending';
  return 'complete';
}

/** Sort key: fire, full, tilt, battery, awaiting pickup, out of service, rest. */
export function containerSeverity(container: Pick<ContainerMapItem, 'status' | 'alarms'>): number {
  const alarms = containerAlarms(container);
  if (alarms.length > 0) return ALARM_ORDER.indexOf(alarms[0]);
  if (isAwaitingPickup(container)) return 4;
  if (isOutOfService(container)) return 5;
  return 6;
}

export function needsAttention(container: Pick<ContainerMapItem, 'status' | 'alarms'>): boolean {
  return containerAlarms(container).length > 0 || isAwaitingPickup(container);
}

export interface ContainerSummary {
  total: number;
  located: number;
  /** Distinct containers with at least one alarm (the API counts alarms, not containers). */
  withAlarm: number;
  fullAlarm: number;
  awaitingPickup: number;
  outOfService: number;
  /** Fill vs capacity over containers whose capacity unit is known; null otherwise. */
  fillShare: number | null;
}

export function summarizeContainers(containers: readonly ContainerMapItem[]): ContainerSummary {
  let located = 0;
  let withAlarm = 0;
  let fullAlarm = 0;
  let awaitingPickup = 0;
  let outOfService = 0;
  let fill = 0;
  let capacity = 0;
  for (const c of containers) {
    if (isValidCoordinate(c.latitude, c.longitude)) located += 1;
    const alarms = containerAlarms(c);
    if (alarms.length > 0) withAlarm += 1;
    if (alarms.includes('full')) fullAlarm += 1;
    if (isAwaitingPickup(c)) awaitingPickup += 1;
    if (isOutOfService(c)) outOfService += 1;
    const cap = capacityInKg(c.capacity, c.capacity_unit);
    if (cap !== null && typeof c.fill_level === 'number' && Number.isFinite(c.fill_level)) {
      capacity += cap;
      fill += Math.max(c.fill_level, 0);
    }
  }
  return {
    total: containers.length,
    located,
    withAlarm,
    fullAlarm,
    awaitingPickup,
    outOfService,
    fillShare: capacity > 0 ? fill / capacity : null,
  };
}

export function sortContainersBySeverity<T extends Pick<ContainerMapItem, 'status' | 'alarms' | 'container_name'>>(
  containers: readonly T[],
): T[] {
  return [...containers].sort(
    (a, b) => containerSeverity(a) - containerSeverity(b) || (a.container_name ?? '').localeCompare(b.container_name ?? ''),
  );
}
